import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card} from 'antd';
import {PaymentMethods, SalesExecutive, UpdateAccountInput} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {DatePicker, Form, FormItem, Input, Select, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import humanize from 'humanize-string';
import {formatDate, formatHumanName} from 'lib/formaters';
import moment from 'moment/moment';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import * as yup from 'yup';
import {omitEmpty} from '../../lib/omitEmpty';
import {SectionProps} from './types';

const {TextArea} = Input;

export const SalesDetails: React.FC<SectionProps> = ({cardProps, formProps, account, onSubmit}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();

  if (!account) return <Card {...cardProps} title="Sales details" />;

  const initialValues: UpdateAccountInput = {
    id: account.id,
    accountExecutive: account.accountExecutive || SalesExecutive.SELF_SERVED,
    upsell: {
      executive: account.upsell?.executive || '',
      paymentMethods: account.upsell?.paymentMethods!,
      date: account.upsell?.date || ''
    },
    note: ''
  };

  const schema = yup.object().shape({
    accountExecutive: yup
      .string()
      .oneOf(Object.values(SalesExecutive))
      .required(),
    upsell: yup.object().shape({
      executive: yup.string().oneOf(Object.values(SalesExecutive)),
      paymentMethods: yup
        .array()
        .of(yup.string().oneOf(Object.values(PaymentMethods)))
        .min(1, 'At least one payment method is required')
        .when('executive', {
          is: (executive: string) => !!executive,
          then: yup.array().required(),
          otherwise: yup.array()
        }),
      date: yup.date().when('executive', {
        is: (executive: string) => !!executive,
        then: yup.date().required(),
        otherwise: yup.date()
      })
    }),
    note: yup.string().when('accountExecutive', {
      is: (accountExecutive: string) => accountExecutive !== initialValues.accountExecutive &&accountExecutive !== SalesExecutive.SELF_SERVED,
      then: yup
        .string()
        .required()
        .min(20),
        otherwise: yup.string()
      })
      .when('upsell.executive', {
        is: (executive: string) => executive && executive !== initialValues.upsell?.executive,
        then: yup.string().required()
      })
  });

  const handleSubmit = async (values: UpdateAccountInput) => {
    await onSubmit(omitEmpty(values));
    setEditing(false);
  };

  return (
    <Card
      {...cardProps}
      title="Sales details"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> {account.accountExecutive ? 'Edit data' : 'Add data'}
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={schema}>
          <Form {...formProps}>
            <FormItem name="accountExecutive" label="Account Executive">
              <Select name="accountExecutive" placeholder="Select option" allowClear>
                {Object.values(SalesExecutive).map((accountExecutive: string) => (
                  <Select.Option value={accountExecutive}>
                    {formatHumanName(accountExecutive)}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
            <FormItem name="upsell.executive" label="Upsell by">
              <Select name="upsell.executive" placeholder="Select option" allowClear>
                {Object.values(SalesExecutive)
                  .filter((accountExecutive) => accountExecutive !== SalesExecutive.SELF_SERVED)
                  .map((accountExecutive: string) => (
                    <Select.Option value={accountExecutive}>
                      {formatHumanName(accountExecutive)}
                    </Select.Option>
                  ))}
              </Select>
            </FormItem>
            <FormItem name="upsell.paymentMethods" label="Upsell Payment methods">
              <Select
                name="upsell.paymentMethods"
                placeholder="Select options"
                mode="multiple"
                allowClear>
                {Object.values(PaymentMethods).map((paymentMethods) => (
                  <Select.Option value={paymentMethods}>{humanize(paymentMethods)}</Select.Option>
                ))}
              </Select>
            </FormItem>
            <FormItem name="upsell.date" label="Upsell date">
              <DatePicker
                name="upsell.date"
                format="L"
                style={{width: '100%'}}
                allowClear={false}
                showToday={false}
                disabledDate={(date) => (date ? date.isAfter(moment() as any) : false)}
              />
            </FormItem>
            <FormItem hasFeedback={false} name="note" label="Note" help={<p>Assigning a lead to you, should be justified in order to keep a good track of each account and to avoid misunderstandings between sales team members. You need to justify: why you assigned the account to you, where the lead came from, and what steps did you take in order to close the lead and onboard it (the note will appear in the timeline on the right).</p>}>
              <TextArea name="note" placeholder="Write a note" allowClear />
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton disabled={false}>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <PropertyList
          omitEmpty
          emptyText="No sales details provided"
          properties={[
            {
              label: 'Account executive',
              value: account.accountExecutive && (
                <Link
                  to={
                    '/accounts/?accountExecutive=' + encodeURIComponent(account.accountExecutive)
                  }>
                  {formatHumanName(account.accountExecutive)}
                </Link>
              )
            },
            {
              label: 'Upsell',
              value: formatUpsell(account.upsell)
            }
          ]}
        />
      )}
    </Card>
  );
};

function formatUpsell(upsell: any) {
  if (!upsell) return null;
  const formattedPaymentMethods = upsell.paymentMethods.map(humanize).join(', ');
  const formattedExecutive = formatHumanName(upsell.executive);
  const formattedDate = formatDate(upsell.date, moment.ISO_8601, true);
  return `${formattedPaymentMethods} by ${formattedExecutive} on ${formattedDate}`;
}
